import { useState, useEffect, useRef, useCallback, type FormEvent } from "react";
import { useAuth } from "@/context/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Store, Users, TrendingUp, CreditCard, Trash2, CalendarPlus, Globe, ShoppingBag, QrCode, Download, Copy, Check, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";
import { QRCodeCanvas } from "qrcode.react";
import { AdminDataCases } from "@/components/admin-data-cases";
import { AdminPartnerAds } from "@/components/admin-partner-ads";
import { AdminTransferDateCorrection } from "@/components/admin-transfer-date-correction";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useListAdminSubscriptionDiscounts, getListAdminSubscriptionDiscountsQueryKey, useSetAdminSubscriptionDiscount, useEndAdminSubscriptionDiscount } from "@workspace/api-client-react";
import type { AdminStore, BusinessSubscriptionDiscount } from "@workspace/api-client-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const formatSubscriptionDate = (value: Date | string) => new Intl.DateTimeFormat("en-US", {
  timeZone: "Africa/Lagos", year: "numeric", month: "short", day: "numeric",
}).format(new Date(value));
const todayInWAT = () => {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Africa/Lagos", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const value = (type: string) => parts.find(part => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
};

type StoreData = AdminStore;
type StoreFilter = "all" | "customers" | "demos";
type ManualTransferForm = {
  plan: "basic" | "pro" | "enterprise";
  billingCycle: "monthly" | "quarterly" | "annual";
  amount: string;
  startsAt: string;
  endsAt: string;
};

interface AdminStats {
  totalStores: number;
  activeSubscriptions: number;
  onTrial: number;
  totalRevenue: number;
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  active: "default",
  trial: "secondary",
  expired: "destructive",
  cancelled: "outline",
};

export default function Admin() {
  const { user } = useAuth();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clearingProducts, setClearingProducts] = useState(false);
  const [storeFilter, setStoreFilter] = useState<StoreFilter>("all");
  const [manualTransferStore, setManualTransferStore] = useState<StoreData | null>(null);
  const [manualTransfer, setManualTransfer] = useState<ManualTransferForm>({
    plan: "basic",
    billingCycle: "monthly",
    amount: "",
    startsAt: todayInWAT(),
    endsAt: "",
  });
  const [savingManualTransfer, setSavingManualTransfer] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [storesRes, statsRes] = await Promise.all([
        fetch(`${BASE}/api/admin/stores`, { credentials: "include" }),
        fetch(`${BASE}/api/admin/stats`, { credentials: "include" }),
      ]);
      if (!storesRes.ok) { setError("Access denied"); return; }
      setStores(await storesRes.json());
      setStats(await statsRes.json());
    } catch {
      setError("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const updateStore = async (id: number, updates: Record<string, any>) => {
    const res = await fetch(`${BASE}/api/admin/stores/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || "Update failed");
      return;
    }
    fetchData();
  };

  const extendTrial = (id: number) => {
    const days = window.prompt("How many days to add to trial?", "3");
    if (!days || isNaN(Number(days))) return;
    const trialEndsAt = new Date(Date.now() + Number(days) * 24 * 60 * 60 * 1000).toISOString();
    updateStore(id, { subscriptionStatus: "trial", trialEndsAt });
  };

  const changePlan = (store: StoreData, plan: string) => {
    if (plan === store.plan) return;
    const formatPlan = (value: string) => value === "trial"
      ? "Trial"
      : value.charAt(0).toUpperCase() + value.slice(1);
    const confirmed = window.confirm(
      `Change ${store.name} from ${formatPlan(store.plan)} to ${formatPlan(plan)}? This changes plan access only. It does not collect a payment or change subscription status, billing history, or an existing Paystack renewal.`,
    );
    if (confirmed) void updateStore(store.id, { plan });
  };

  const openManualTransfer = (store: StoreData) => {
    setManualTransferStore(store);
    setManualTransfer({
      plan: store.plan === "basic" || store.plan === "pro" || store.plan === "enterprise"
        ? store.plan
        : "basic",
      billingCycle: "monthly",
      amount: "",
      startsAt: todayInWAT(),
      endsAt: "",
    });
  };

  const recordManualTransfer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!manualTransferStore) return;
    setSavingManualTransfer(true);
    try {
      const res = await fetch(`${BASE}/api/admin/stores/${manualTransferStore.id}/manual-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...manualTransfer,
          amount: Number(manualTransfer.amount),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || "Could not record the bank transfer");
        return;
      }
      setManualTransferStore(null);
      await fetchData();
      alert("Bank transfer recorded and subscription dates updated.");
    } catch {
      alert("Could not reach the server. The transfer was not recorded.");
    } finally {
      setSavingManualTransfer(false);
    }
  };

  const visibleStores = stores.filter(store => {
    if (storeFilter === "customers") return !store.isDemo;
    if (storeFilter === "demos") return store.isDemo;
    return true;
  });

  const clearAllProducts = async () => {
    if (!window.confirm("⚠️ This will delete ALL products across the entire system (products are shared globally). Are you sure?")) return;
    setClearingProducts(true);
    try {
      const res = await fetch(`${BASE}/api/admin/products`, { method: "DELETE", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(data.error || "Failed to clear products"); return; }
      alert(`Deleted ${data.deleted} products.`);
    } finally {
      setClearingProducts(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (error) return <div className="flex items-center justify-center h-full"><p className="text-destructive">{error}</p></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Super Admin</h1>
          <p className="text-muted-foreground mt-1">Manage all RetailPulse subscribers and stores.</p>
        </div>
        <Button variant="destructive" size="sm" className="gap-2" onClick={clearAllProducts} disabled={clearingProducts}>
          {clearingProducts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          Clear All Products
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Total Stores</CardTitle>
              <Store className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{stats.totalStores}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Active Subscribers</CardTitle>
              <CreditCard className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{stats.activeSubscriptions}</div>
              <p className="text-xs text-muted-foreground mt-1">Excludes demo accounts</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">On Trial</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.onTrial}</div>
              <p className="text-xs text-muted-foreground mt-1">Excludes demo accounts</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
              <p className="text-xs text-muted-foreground mt-1">Excludes demo accounts</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="stores">
        <TabsList>
          <TabsTrigger value="stores" className="gap-1.5"><Store className="w-3.5 h-3.5" />POS Stores</TabsTrigger>
          <TabsTrigger value="data-cases">Data deadlines</TabsTrigger>
          <TabsTrigger value="discounts">Discounts</TabsTrigger>
          <TabsTrigger value="partner-ads">Partner Ads</TabsTrigger>
          <TabsTrigger value="online-stores" className="gap-1.5"><Globe className="w-3.5 h-3.5" />Online Stores</TabsTrigger>
          <TabsTrigger value="waitlist" className="gap-1.5"><ShoppingBag className="w-3.5 h-3.5" />E-Commerce Waitlist</TabsTrigger>
          <TabsTrigger value="share" className="gap-1.5"><QrCode className="w-3.5 h-3.5" />Share & QR Code</TabsTrigger>
        </TabsList>

        {/* Share & QR Code */}
        <TabsContent value="share">
          <SignupQRCode />
        </TabsContent>

        {/* POS Stores */}
        <TabsContent value="data-cases"><AdminDataCases stores={stores} /></TabsContent>
        <TabsContent value="stores">
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>
                  {storeFilter === "demos" ? "Demo Accounts" : storeFilter === "customers" ? "Customer Accounts" : "All Stores"} ({visibleStores.length})
                </CardTitle>
                <CardDescription>Plan changes affect access only; existing billing stays unchanged.</CardDescription>
              </div>
              <Select value={storeFilter} onValueChange={(value) => setStoreFilter(value as StoreFilter)}>
                <SelectTrigger className="w-full sm:w-48" aria-label="Filter stores">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  <SelectItem value="customers">Customer accounts</SelectItem>
                  <SelectItem value="demos">Demo accounts</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Store</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>POS Products</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Trial / Sub</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleStores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No accounts match this filter.
                      </TableCell>
                    </TableRow>
                  ) : visibleStores.map(store => (
                    <TableRow key={store.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium flex flex-wrap items-center gap-2">
                            {store.name}
                            {store.isDemo && <Badge variant="secondary">Demo</Badge>}
                          </p>
                          <p className="text-xs text-muted-foreground">{store.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{store.phone ?? <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                      <TableCell>
                        <Select value={store.plan} onValueChange={(plan) => changePlan(store, plan)}>
                          <SelectTrigger className="h-7 w-32 text-xs capitalize" aria-label={`Plan for ${store.name}`}>
                            <SelectValue placeholder={store.plan} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[store.subscriptionStatus] || "outline"}>
                          {store.subscriptionStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>{store.productCount.toLocaleString()}</TableCell>
                      <TableCell>{store.userCount}</TableCell>
                       <TableCell className="text-xs text-muted-foreground">
                         {store.subscriptionStatus === "trial"
                            ? `Trial ends ${formatSubscriptionDate(store.trialEndsAt)}`
                           : store.latestSubscription
                           ? (
                             <div className="space-y-0.5">
                               <div>
                                 {formatCurrency(store.latestSubscription.amount)}
                                  {store.latestSubscription.paymentMethod === "manual_transfer"
                                    ? " received"
                                    : store.latestSubscription.billingCycle === "annual"
                                   ? "/year"
                                   : store.latestSubscription.billingCycle === "quarterly"
                                   ? "/quarter"
                                   : store.latestSubscription.billingCycle === "promo"
                                   ? " · promo"
                                   : "/month"}
                               </div>
                                <div>
                                   Period {formatSubscriptionDate(store.latestSubscription.startsAt)}
                                  {store.latestSubscription.endsAt
                                     ? ` – ${formatSubscriptionDate(store.latestSubscription.endsAt)}`
                                    : ""}
                                </div>
                                <div className="capitalize">
                                  {store.latestSubscription.paymentMethod === "manual_transfer"
                                    ? "Bank transfer"
                                    : store.latestSubscription.paymentMethod === "complimentary"
                                    ? "Complimentary"
                                    : store.latestSubscription.billingCycle === "promo"
                                    ? "Promotional offer"
                                    : "Online payment"}
                                </div>
                               {store.latestSubscription.endsAt && (
                                  <div>Expires {formatSubscriptionDate(store.latestSubscription.endsAt)}</div>
                               )}
                             </div>
                           )
                           : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{format(new Date(store.createdAt), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Select onValueChange={(val) => updateStore(store.id, { subscriptionStatus: val })}>
                            <SelectTrigger className="h-7 w-28 text-xs">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="trial">Set Trial</SelectItem>
                              <SelectItem value="active">Set Active</SelectItem>
                              <SelectItem value="expired">Set Expired</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" title="Set custom trial days" onClick={() => extendTrial(store.id)}>
                            <CalendarPlus className="w-3 h-3" /> +days
                          </Button>
                          {!store.isDemo && (
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openManualTransfer(store)}>
                              Record transfer
                            </Button>
                          )}
                           <AdminTransferDateCorrection store={store} onSaved={fetchData} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discounts"><AdminDiscounts stores={stores} /></TabsContent>
        <TabsContent value="partner-ads"><AdminPartnerAds /></TabsContent>

        {/* Online Stores */}
        <TabsContent value="online-stores">
          <AdminOnlineStores />
        </TabsContent>

        {/* Waitlist */}
        <TabsContent value="waitlist">
          <AdminWaitlist />
        </TabsContent>
      </Tabs>
      <Dialog open={manualTransferStore !== null} onOpenChange={open => { if (!open && !savingManualTransfer) setManualTransferStore(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record subscription bank transfer</DialogTitle>
            <DialogDescription>
              {manualTransferStore?.name} — enter the confirmed amount and paid period. Start date must be today or earlier; access lasts through 6:00 PM WAT on the end date. No Paystack renewal is created.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={recordManualTransfer} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span>Plan</span>
                <Select value={manualTransfer.plan} onValueChange={value => setManualTransfer(form => ({ ...form, plan: value as ManualTransferForm["plan"] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1 text-sm">
                <span>Billing cycle</span>
                <Select value={manualTransfer.billingCycle} onValueChange={value => setManualTransfer(form => ({ ...form, billingCycle: value as ManualTransferForm["billingCycle"] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1 text-sm">
                <span>Amount received (₦)</span>
                <Input type="number" min="0.01" step="0.01" required value={manualTransfer.amount} onChange={event => setManualTransfer(form => ({ ...form, amount: event.target.value }))} />
              </label>
              <label className="space-y-1 text-sm">
                <span>Subscription start date</span>
                <Input type="date" max={todayInWAT()} required value={manualTransfer.startsAt} onChange={event => setManualTransfer(form => ({ ...form, startsAt: event.target.value }))} />
              </label>
              <label className="space-y-1 text-sm">
                <span>Subscription end date</span>
                <Input type="date" min={manualTransfer.startsAt || undefined} required value={manualTransfer.endsAt} onChange={event => setManualTransfer(form => ({ ...form, endsAt: event.target.value }))} />
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={savingManualTransfer} onClick={() => setManualTransferStore(null)}>Cancel</Button>
              <Button type="submit" disabled={savingManualTransfer}>
                {savingManualTransfer ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save transfer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface WaitlistEntry {
  id: number; name: string; email: string; business_type: string | null;
  community: string | null; message: string | null; created_at: string;
}
interface AdminOnlineStore {
  id: number; storeId: number; slug: string; displayName: string; isPublished: boolean;
  primaryColor: string; createdAt: string; productCount: number; orderCount: number;
}

function AdminWaitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/api/ecommerce/admin/waitlist`, { credentials: "include" })
      .then(r => r.json()).then(d => setEntries(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>E-Commerce Waitlist ({entries.length})</CardTitle>
          <span className="text-sm text-muted-foreground">{entries.length} sign-up{entries.length !== 1 ? "s" : ""}</span>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground text-sm">No waitlist entries yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Business Type</TableHead>
                <TableHead>Community</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Signed Up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{e.email}</TableCell>
                  <TableCell>{e.business_type ? <Badge variant="outline" className="capitalize">{e.business_type}</Badge> : "—"}</TableCell>
                  <TableCell className="text-sm">{e.community || "—"}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate text-muted-foreground">{e.message || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{format(new Date(e.created_at), "MMM d, yyyy")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Signup QR Code ───────────────────────────────────────────────────────────

const BUSINESS_LINKS = [
  { label: "General Signup", path: "/signup", color: "#6366f1" },
  { label: "Supermarket", path: "/signup?type=supermarket", color: "#10b981" },
  { label: "Fashion & Clothing", path: "/signup?type=fashion", color: "#ec4899" },
  { label: "Food & Restaurant", path: "/signup?type=food", color: "#f59e0b" },
  { label: "Salon & Spa", path: "/signup?type=salon", color: "#8b5cf6" },
  { label: "Fashion Designer", path: "/signup?type=tailor", color: "#06b6d4" },
];

function SignupQRCode() {
  const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, "");
  const [selected, setSelected] = useState(0);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const origin = window.location.origin;
  const link = BUSINESS_LINKS[selected];
  const fullUrl = `${origin}${BASE_PATH}${link.path}`;

  const download = useCallback(() => {
    const canvas = document.querySelector("#signup-qr canvas") as HTMLCanvasElement | null;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `retailpulse-signup-qr-${link.label.toLowerCase().replace(/\s+/g, "-")}.png`;
    a.click();
  }, [link.label]);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [fullUrl]);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Left — QR display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5" /> Signup QR Code
          </CardTitle>
          <CardDescription>Scan to go directly to the RetailPulse signup page. Print and share with potential customers.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-5">
          {/* QR Code */}
          <div id="signup-qr" className="p-5 bg-white rounded-2xl border-2 shadow-sm" style={{ borderColor: link.color }}>
            <QRCodeCanvas
              ref={canvasRef}
              value={fullUrl}
              size={220}
              bgColor="#ffffff"
              fgColor="#0f172a"
              level="H"
              imageSettings={{
                src: `${BASE_PATH}/logo.png`,
                x: undefined,
                y: undefined,
                height: 40,
                width: 40,
                excavate: true,
              }}
            />
          </div>

          {/* Label below QR */}
          <div className="text-center">
            <p className="font-semibold text-sm">{link.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Start your free 10-day trial</p>
          </div>

          {/* URL pill */}
          <div className="flex w-full gap-2">
            <Input value={fullUrl} readOnly className="text-xs font-mono h-8 bg-muted/50" />
            <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5 shrink-0" onClick={copyLink}>
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>

          {/* Actions */}
          <div className="flex gap-2 w-full">
            <Button className="flex-1 gap-2" onClick={download}>
              <Download className="w-4 h-4" /> Download PNG
            </Button>
            <a href={fullUrl} target="_blank" rel="noreferrer" className="flex-1">
              <Button variant="outline" className="w-full gap-2">
                <ExternalLink className="w-4 h-4" /> Open Link
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Right — Link selector */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Choose Signup Link</CardTitle>
            <CardDescription>Generate a QR code for a specific business type so sign-ups are pre-configured.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {BUSINESS_LINKS.map((bl, i) => (
              <button
                key={i}
                onClick={() => setSelected(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                  selected === i ? "border-primary bg-primary/5" : "border-border hover:border-border/80 hover:bg-muted/40"
                }`}
              >
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: bl.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{bl.label}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{bl.path}</p>
                </div>
                {selected === i && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <h4 className="text-sm font-semibold mb-2">How to use</h4>
            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li>• <strong>Print</strong> the QR code and display it at trade shows, markets, or on flyers</li>
              <li>• <strong>Share the link</strong> on WhatsApp, Instagram, or via SMS to potential customers</li>
              <li>• <strong>Business-type links</strong> pre-select the store type on signup for a smoother onboarding</li>
              <li>• The RetailPulse logo is embedded in the QR for brand recognition</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdminOnlineStores() {
  const [stores, setStores] = useState<AdminOnlineStore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/api/ecommerce/admin/stores`, { credentials: "include" })
      .then(r => r.json()).then(d => setStores(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Online Stores ({stores.length})</CardTitle>
          <a href="/shop" target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="gap-1.5 text-xs"><Globe className="w-3.5 h-3.5" /> View Marketplace</Button>
          </a>
        </div>
      </CardHeader>
      <CardContent>
        {stores.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground text-sm">No online stores set up yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Store</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map(s => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.displayName}</TableCell>
                  <TableCell>
                    <a href={`/shop/${s.slug}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline font-mono">/shop/{s.slug}</a>
                  </TableCell>
                  <TableCell>
                    <Badge className={s.isPublished ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600"}>
                      {s.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell>{s.productCount}</TableCell>
                  <TableCell>{s.orderCount}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{format(new Date(s.createdAt), "MMM d, yyyy")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Subscription discounts ───────────────────────────────────────────────────

const D_PLANS = ["basic", "pro", "enterprise"] as const;
const D_CYCLES = ["monthly", "quarterly", "annual"] as const;

function errMsg(e: unknown): string {
  const x = e as { data?: { error?: string }; message?: string } | null;
  return x?.data?.error || x?.message || "Request failed";
}

function AdminDiscounts({ stores }: { stores: StoreData[] }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useListAdminSubscriptionDiscounts();
  const setDiscount = useSetAdminSubscriptionDiscount();
  const endDiscount = useEndAdminSubscriptionDiscount();
  const [storeId, setStoreId] = useState<number | null>(null);
  const [planId, setPlanId] = useState<(typeof D_PLANS)[number]>("basic");
  const [cycle, setCycle] = useState<(typeof D_CYCLES)[number]>("monthly");
  const [percent, setPercent] = useState("");
  const [payments, setPayments] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");

  const latest = new Map<number, BusinessSubscriptionDiscount>();
  (data ?? []).forEach(d => {
    const cur = latest.get(d.storeId);
    if (!cur || d.id > cur.id) latest.set(d.storeId, d);
  });

  const refresh = () => qc.invalidateQueries({ queryKey: getListAdminSubscriptionDiscountsQueryKey() });

  const openForm = (s: StoreData) => {
    const d = latest.get(s.id);
    setStoreId(s.id); setFormError(""); setNotice("");
    setPlanId((D_PLANS as readonly string[]).includes(d?.planId ?? "") ? (d!.planId as (typeof D_PLANS)[number]) : "basic");
    setCycle((D_CYCLES as readonly string[]).includes(d?.billingCycle ?? "") ? (d!.billingCycle as (typeof D_CYCLES)[number]) : "monthly");
    setPercent(d ? String(d.discountPercent) : "");
    setPayments(d ? String(d.maxPayments) : "");
    setReason(d?.reason ?? "");
  };

  const save = () => {
    if (storeId == null) return;
    const p = Number(percent), n = Number(payments);
    if (!Number.isInteger(p) || p < 1 || p > 99) return setFormError("Discount must be a whole number from 1 to 99.");
    if (!Number.isInteger(n) || n < 1 || n > 36) return setFormError("Discounted payments must be a whole number from 1 to 36.");
    setFormError("");
    setDiscount.mutate(
      { storeId, data: { planId, billingCycle: cycle, discountPercent: p, maxPayments: n, ...(reason.trim() ? { reason: reason.trim() } : {}) } },
      {
        onSuccess: () => { setNotice("Offer saved. It applies to the next matching checkout only."); setStoreId(null); refresh(); },
        onError: e => setFormError(errMsg(e)),
      },
    );
  };

  const end = (s: StoreData) => {
    if (!window.confirm(`End the offer for ${s.name}? Existing purchases are not changed.`)) return;
    setNotice("");
    endDiscount.mutate({ storeId: s.id }, {
      onSuccess: () => { setNotice(`Offer ended for ${s.name}.`); refresh(); },
      onError: e => setFormError(errMsg(e)),
    });
  };

  const editing = stores.find(s => s.id === storeId);
  const fieldCls = "text-sm font-medium block mb-1";
  const selCls = "w-full h-9 rounded-md border bg-background px-2 text-sm capitalize";

  return (
    <div className="space-y-4">
      <Card className="bg-muted/30">
        <CardContent className="pt-4 pb-4 text-sm text-muted-foreground space-y-1">
          <p><strong className="text-foreground">One offer per business, one checkout.</strong> A discount is redeemed by a single subscription checkout for the selected plan and cycle.</p>
          <p>Saving or ending an offer never changes already-started checkouts or existing subscriptions. Discounted automatic billing stops after the set number of payments. The merchant must start a new checkout at the standard price to renew.</p>
        </CardContent>
      </Card>

      {notice && <div className="text-sm rounded-lg bg-emerald-500/10 text-emerald-700 px-4 py-2" role="status" data-testid="text-discount-notice">{notice}</div>}

      {editing && (
        <Card className="border-primary/40" data-testid="form-discount">
          <CardHeader>
            <CardTitle className="text-base">{latest.get(editing.id)?.isActive ? "Edit offer" : "New offer"} for {editing.name}</CardTitle>
            <CardDescription>Saving replaces any open offer for this business.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div><label className={fieldCls} htmlFor="d-plan">Plan</label>
                <select id="d-plan" className={selCls} value={planId} onChange={e => setPlanId(e.target.value as typeof planId)}>{D_PLANS.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              <div><label className={fieldCls} htmlFor="d-cycle">Cycle</label>
                <select id="d-cycle" className={selCls} value={cycle} onChange={e => setCycle(e.target.value as typeof cycle)}>{D_CYCLES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              <div><label className={fieldCls} htmlFor="d-pct">Discount % (1-99)</label>
                <Input id="d-pct" type="number" inputMode="numeric" min={1} max={99} step={1} value={percent} onChange={e => setPercent(e.target.value)} data-testid="input-discount-percent" /></div>
              <div><label className={fieldCls} htmlFor="d-pay">Discounted payments (1-36)</label>
                <Input id="d-pay" type="number" inputMode="numeric" min={1} max={36} step={1} value={payments} onChange={e => setPayments(e.target.value)} data-testid="input-discount-payments" /></div>
            </div>
            <div><label className={fieldCls} htmlFor="d-reason">Reason (optional)</label>
              <Input id="d-reason" maxLength={120} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. School partnership" data-testid="input-discount-reason" />
              <p className="text-xs text-muted-foreground mt-1">{reason.length}/120</p></div>
            <p className="text-xs text-muted-foreground">One payment covers {cycle === "monthly" ? "one month" : cycle === "quarterly" ? "three months" : "one year"}. The first payment counts toward the limit. A one-payment offer does not enable automatic billing.</p>
            {formError && <p className="text-sm text-destructive" role="alert" data-testid="text-discount-error">{formError}</p>}
            <div className="flex gap-2">
              <Button onClick={save} disabled={setDiscount.isPending} data-testid="button-save-discount">
                {setDiscount.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save offer
              </Button>
              <Button variant="outline" onClick={() => { setStoreId(null); setFormError(""); }} disabled={setDiscount.isPending}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Business offers</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 rounded bg-muted animate-pulse" />)}</div>
          ) : isError ? (
            <div className="text-center py-6 space-y-2">
              <p className="text-sm text-destructive">Could not load offers.</p>
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Store</TableHead>
                  <TableHead>Latest offer</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.map(s => {
                  const d = latest.get(s.id);
                  const state = !d ? null : d.redeemedAt ? "Redeemed" : d.isActive ? "Open" : "Ended";
                  return (
                    <TableRow key={s.id} data-testid={`row-discount-${s.id}`}>
                      <TableCell><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></TableCell>
                      <TableCell className="text-sm">
                        {d ? (
                          <div>
                            <p><strong>{d.discountPercent}%</strong> off <span className="capitalize">{d.planId} {d.billingCycle}</span> for {d.maxPayments} payment{d.maxPayments !== 1 ? "s" : ""}</p>
                            {d.reason && <p className="text-xs text-muted-foreground">{d.reason}</p>}
                            <p className="text-xs text-muted-foreground">Created {format(new Date(d.createdAt), "MMM d, yyyy")}{d.redeemedAt ? ` · redeemed ${format(new Date(d.redeemedAt), "MMM d, yyyy")}` : ""}</p>
                          </div>
                        ) : <span className="text-muted-foreground text-xs">No offer</span>}
                      </TableCell>
                      <TableCell>{state && <Badge variant={state === "Open" ? "default" : "outline"}>{state}</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openForm(s)} data-testid={`button-edit-discount-${s.id}`}>{d?.isActive ? "Edit" : "New offer"}</Button>
                          {d?.isActive && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-destructive" onClick={() => end(s)} disabled={endDiscount.isPending} data-testid={`button-end-discount-${s.id}`}>End offer</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
